import { useState, useMemo } from 'react'
import { Alert, Button, Drawer, Input } from 'antd'
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import { useSurveyResponses } from '../hooks/useSurveyResponses'
import { SurveyStatsCards } from './SurveyStatsCards'
import { SurveyResponsesTable } from './SurveyResponsesTable'
import { SurveyResponseDetail } from './SurveyResponseDetail'
import { exportResponsesToCsv } from '../utils/export-responses'
import type { SurveyResponseItem } from '../types/survey.types'

interface SurveyResponsesDrawerProps {
  open: boolean
  surveyId: string | null
  surveyTitle: string | null
  onClose: () => void
}

export function SurveyResponsesDrawer({ open, surveyId, surveyTitle, onClose }: SurveyResponsesDrawerProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedResponse, setSelectedResponse] = useState<SurveyResponseItem | null>(null)

  const { responses, stats, questions, loading, error, refresh } = useSurveyResponses(open ? surveyId : null)

  const filteredResponses = useMemo(() => {
    if (!searchTerm.trim()) return responses

    const search = searchTerm.toLowerCase().trim()
    return responses.filter(
      r =>
        r.userId.toLowerCase().includes(search) ||
        r.tenantCode.toLowerCase().includes(search) ||
        (r.userEmail?.toLowerCase().includes(search) ?? false) ||
        (r.tenantName?.toLowerCase().includes(search) ?? false)
    )
  }, [responses, searchTerm])

  const handleRowClick = (response: SurveyResponseItem) => {
    setSelectedResponse(response)
    setDetailOpen(true)
  }

  const handleDetailClose = () => {
    setDetailOpen(false)
    setSelectedResponse(null)
  }

  const handleExport = () => {
    if (!surveyId || responses.length === 0) return
    exportResponsesToCsv(responses, questions, surveyId)
  }

  return (
    <>
      <Drawer title={`Responses \u2014 ${surveyTitle || ''}`} open={open} onClose={onClose} width={1200} extra={null}>
        {error ? (
          <Alert
            message="Error Loading Responses"
            description={error.message}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={refresh}>
                Retry
              </Button>
            }
          />
        ) : loading && responses.length === 0 ? (
          <Loading />
        ) : (
          <>
            <SurveyStatsCards stats={stats} loading={loading} />

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Input
                placeholder="Search by user or tenant..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={responses.length === 0}>
                Export CSV
              </Button>
            </div>

            {filteredResponses.length === 0 && searchTerm ? (
              <EmptyState description={`No responses found matching "${searchTerm}"`} />
            ) : filteredResponses.length === 0 ? (
              <EmptyState description="No survey responses yet" />
            ) : (
              <SurveyResponsesTable
                responses={filteredResponses}
                questions={questions}
                loading={loading}
                onRowClick={handleRowClick}
              />
            )}
          </>
        )}
      </Drawer>

      <SurveyResponseDetail
        response={selectedResponse}
        questions={questions}
        open={detailOpen}
        onClose={handleDetailClose}
      />
    </>
  )
}
